import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import '../zap-automations/zap-tour.scss';
import { FeatureGuide } from './guideContent';

/* "How to use <feature>" — the same walkthrough frame as the Zap guide
   (zap-automations/ZapTourModal.tsx) and the same stylesheet, driven by
   guideContent.ts so Storefront and Link in Bio share one component. */

interface Props {
    open: boolean;
    onClose: () => void;
    guide: FeatureGuide;
}

const FeatureGuideModal: React.FC<Props> = ({ open, onClose, guide }) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const firstId = guide.sections[0]?.id;
    const [progress, setProgress] = useState(0);
    const [showToTop, setShowToTop] = useState(false);
    const [activeId, setActiveId] = useState<string>(firstId);
    const [navOpen, setNavOpen] = useState(false);
    const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

    // Same deterministic active-section tracking as the Zap guide.
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const max = el.scrollHeight - el.clientHeight;
        setProgress(max > 0 ? (el.scrollTop / max) * 100 : 0);
        setShowToTop(el.scrollTop > 500);
        const line = el.scrollTop + 140;
        let current = firstId;
        el.querySelectorAll<HTMLElement>('[data-section]').forEach((s) => {
            if (s.offsetTop <= line) current = s.dataset.section || current;
        });
        setActiveId(current);
    }, [firstId]);

    const scrollToSection = useCallback((id: string) => {
        const el = scrollRef.current;
        if (!el) return;
        const target = el.querySelector<HTMLElement>(`[data-section="${id}"]`);
        if (target) el.scrollTo({ top: Math.max(target.offsetTop - 56, 0), behavior: 'smooth' });
        setNavOpen(false);
    }, []);

    const scrollToTop = () => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

    useEffect(() => {
        if (open) {
            setProgress(0);
            setShowToTop(false);
            setActiveId(firstId);
            setLightbox(null);
        }
    }, [open, firstId]);

    // Close the lightbox on Escape before the modal itself closes.
    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setLightbox(null);
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [lightbox]);

    const openLightbox = useCallback((src: string, alt: string) => setLightbox({ src, alt }), []);

    // Stable identity across scroll-driven re-renders (see ZapTourModal).
    const Shot = useMemo(() => {
        const Component: React.FC<{ src: string; caption: string }> = ({ src, caption }) => (
            <figure className="shot">
                <div className="phone phone-sm" onClick={() => openLightbox(src, caption)} role="button" aria-label={`Expand: ${caption}`}>
                    <div className="phone-notch" />
                    <img src={src} alt={caption} />
                </div>
                <figcaption>{caption}</figcaption>
            </figure>
        );
        return Component;
    }, [openLightbox]);

    // The first screenshot doubles as the hero phone.
    const heroShot = guide.sections.find((s) => s.shot)?.shot;

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="feature-guide-title">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95vw',
                    maxWidth: 1100,
                    height: '90vh',
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: 24,
                    outline: 'none',
                }}
            >
                <IconButton
                    onClick={onClose}
                    aria-label="Close guide"
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 12,
                        zIndex: 1200,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        boxShadow: 1,
                        '&:hover': { bgcolor: '#fff' },
                    }}
                >
                    <CloseRounded />
                </IconButton>

                <div className="zap-tour-guide" ref={scrollRef} onScroll={handleScroll}>
                    <div className="scroll-progress" style={{ width: `${progress}%` }} />

                    <header className="topbar">
                        <button
                            className="brand"
                            onClick={scrollToTop}
                            style={{ background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                            <span className="bolt" aria-hidden="true">⚡</span>
                            <span className="brand-text" id="feature-guide-title">
                                ClanConnect <strong>{guide.name}</strong>
                            </span>
                        </button>
                        <nav className={`topnav${navOpen ? ' show' : ''}`}>
                            {guide.sections.map((s) => (
                                <button key={s.id} className={activeId === s.id ? 'active' : ''} onClick={() => scrollToSection(s.id)}>
                                    {s.nav}
                                </button>
                            ))}
                        </nav>
                        <button className="nav-toggle" aria-label="Menu" onClick={() => setNavOpen((v) => !v)}>
                            ☰
                        </button>
                    </header>

                    <section className="hero">
                        <div className="hero-inner">
                            <div className="hero-copy">
                                <h1>
                                    How to use <span className="grad">{guide.name}</span>
                                </h1>
                                <p className="lead">{guide.lead}</p>
                                <p className="muted">{guide.timeNote}</p>
                                <div className="hero-cta">
                                    <button className="btn btn-primary" onClick={() => scrollToSection(firstId)}>
                                        Show me how
                                    </button>
                                </div>
                            </div>
                            {heroShot && (
                                <div className="hero-phone">
                                    <div className="phone" onClick={() => openLightbox(heroShot.src, heroShot.caption)} role="button">
                                        <div className="phone-notch" />
                                        <img src={heroShot.src} alt={heroShot.caption} />
                                    </div>
                                    <div className="phone-glow" />
                                </div>
                            )}
                        </div>
                    </section>

                    <main>
                        {guide.sections.map((s, i) => {
                            const copy = (
                                <div className="split-copy">
                                    {s.kicker && <span className="kicker">{s.kicker}</span>}
                                    <h2>{s.title}</h2>
                                    {s.body && <p>{s.body}</p>}
                                    {s.steps && (
                                        <ol className="steps">
                                            {s.steps.map((t, j) => <li key={j}>{t}</li>)}
                                        </ol>
                                    )}
                                    {s.choices && (
                                        <ul className="ticks">
                                            {s.choices.map(([title, desc]) => (
                                                <li key={title}><strong>{title}</strong> — {desc}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {s.tips?.map((t, j) => (
                                        <div key={j} className="callout callout-info">
                                            <span className="callout-icon">{t.icon}</span>
                                            <div>{t.text}</div>
                                        </div>
                                    ))}
                                </div>
                            );
                            return (
                                <section key={s.id} className={`chapter${i % 2 === 1 ? ' alt' : ''}`} data-section={s.id}>
                                    {s.flow ? (
                                        <>
                                            <div className="chapter-head">
                                                <h2>{s.title}</h2>
                                            </div>
                                            <div className="flow">
                                                {s.flow.map((f, j) => (
                                                    <React.Fragment key={j}>
                                                        {j > 0 && <div className="flow-arrow">→</div>}
                                                        <div className="flow-step">
                                                            {/* OLD: {f.icon} — emoji on the gradient badge read poorly. */}
                                                            <span className="flow-num">{j + 1}</span>
                                                            <p>{f.text}</p>
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </>
                                    ) : s.shot ? (
                                        // Alternate the screenshot side, like the Zap guide.
                                        <div className={`split${i % 2 === 1 ? ' reverse' : ''}`}>
                                            <Shot src={s.shot.src} caption={s.shot.caption} />
                                            {copy}
                                        </div>
                                    ) : (
                                        <div className="split-copy wide">{copy}</div>
                                    )}
                                    {s.success && (
                                        <div className="callout callout-success">
                                            <span className="callout-icon">🎉</span>
                                            <div><strong>{s.success.title.replace(/^🎉\s*/, '')}</strong> {s.success.text}</div>
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </main>

                    <footer className="footer">
                        <p className="muted">Click any screenshot to see it bigger.</p>
                    </footer>

                    <button className={`to-top${showToTop ? ' show' : ''}`} aria-label="Back to top" onClick={scrollToTop}>
                        ↑
                    </button>
                </div>

                {lightbox && (
                    <div className="zap-tour-lightbox" onClick={() => setLightbox(null)}>
                        <button className="lb-close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
                        <img src={lightbox.src} alt={lightbox.alt} onClick={(e) => e.stopPropagation()} />
                    </div>
                )}
            </Box>
        </Modal>
    );
};

export default FeatureGuideModal;
